import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { BRAND_ASSETS, TENANT } from '../config';
import EmergePrivacyPolicy from './emerge/EmergePrivacyPolicy';

const PrivacyPolicy: React.FC = () => {
  const isRedexBuild = process.env.REACT_APP_TENANT_ID === 'redex';
  const platformName = TENANT.branding.name;
  const navigate = useNavigate();
  // Opened in a new tab (e.g. from the sign-up consent links) there's no history to go
  // back to — fall back to home so the user is never stranded on the page.
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  // eMerge is a healthcare business. The policy below describes a securities
  // business — it collects investor accreditation and suitability and never
  // mentions health information — so serving it to a patient would misstate
  // what we collect. A privacy policy is a public statement people rely on,
  // so the wrong one is worse than a missing one.
  if (TENANT.id === 'emerge') {
    return <EmergePrivacyPolicy />;
  }

  if (isRedexBuild) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="bg-white border-b border-neutral-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-2 text-neutral-600 hover:text-neutral-900">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back to Home</span>
              </Link>
              <img src={BRAND_ASSETS.logo.primary} alt={platformName} className="h-8 w-auto" />
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-card border border-neutral-200 p-8 md:p-12"
          >
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">Privacy Policy</h1>
            <p className="text-sm text-neutral-500 mb-8">Last Updated: February 17, 2026</p>

            <div className="prose prose-neutral max-w-none">
              <p className="text-neutral-700 mb-6">
                This Privacy Policy explains how {platformName} collects, uses, and shares information when you access or use the Services.
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">1. Information We Collect</h2>
                <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                  <li>Account information you provide (e.g., name, email, phone).</li>
                  <li>Identity and compliance information when required for certain workflows.</li>
                  <li>Usage and device data (e.g., IP address, browser, log data) for security and analytics.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">2. How We Use Information</h2>
                <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                  <li>Provide and secure the Services.</li>
                  <li>Prevent fraud and abuse.</li>
                  <li>Comply with applicable legal obligations.</li>
                  <li>Improve product performance and reliability.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">3. Sharing</h2>
                <p className="text-neutral-700 mb-4">
                  We may share information with service providers (e.g., identity verification, banking rails, infrastructure) as needed to operate the Services,
                  and when required by law.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">4. Contact</h2>
                <p className="text-neutral-700">
                  Privacy questions can be sent to{' '}
                  <a href={`mailto:${TENANT.branding.supportEmail}`} className="text-primary hover:underline">
                    {TENANT.branding.supportEmail}
                  </a>.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-background border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <img src={BRAND_ASSETS.logo.primary} alt={platformName} className="h-8 w-auto" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div
          className="bg-background rounded-xl shadow-card border border-border p-8 md:p-12"
        >
          <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mb-8">Last Updated: December 8, 2025</p>

          <div className="prose prose-neutral max-w-none font-sans">
            <p className="text-neutral-700 mb-6">
              Your privacy is important to us. This Privacy Policy (this "Policy") applies to services provided by Honeyshares LLC ("Honeyshares," "we," "us," or "our"), as well as our affiliates and subsidiaries, and to our website (the "Site"), mobile app and/or other online platform(s) (collectively, the "Services"). It explains what information we collect from users or visitors of our Services (each, "user," "you," or "your"), including information that may be used to personally identify you ("Personal Information"), and how we use, share, and protect it.
            </p>
            <p className="text-neutral-700 mb-6">
              By accessing or using the Services, you agree to the collection, use, and sharing of your information as described in this Policy. If you do not agree with this Policy, you must stop using the Services.
            </p>
            <p className="text-neutral-700 mb-6">
              We will be the controller of your Personal Information provided to, collected by, or for, or processed in connection with our Services.
            </p>
            <p className="text-neutral-700 mb-8">
              We reserve the right to change this Policy at any time. We will notify you of material changes by sending notice to the primary email address specified in your account or by posting a prominent notice on our Site. Changes will take effect when posted (unless otherwise stated). Your continued use of the Services after we publish or send notice of changes means that your Personal Information is subject to the updated Policy.
            </p>

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">1. What Information Do We Collect?</h2>
              
              <h3 className="text-xl font-semibold text-neutral-800 mb-3">1.1 Information You Provide to Us</h3>
              <p className="text-neutral-700 mb-4">
                We collect information that you knowingly choose to provide when you create an account, communicate with us, or otherwise access or use the Services. This may include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Your full name, email address, residential address, citizenship, telephone number, Social Security number, date of birth, employment information (including status and employer information, if applicable), information regarding investor "accreditation" and suitability, responses to surveys (for research or to help direct Honeyshares activities), and social media account information;</li>
                <li>The contact information of your representative, adviser, or authorized agent (if any); and/or</li>
                <li>Details of any financial transactions you participate in through the Services, including the amount, currency, and method of payment.</li>
              </ul>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">1.2 Information Collected Automatically</h3>
              <p className="text-neutral-700 mb-4">
                We automatically or passively collect certain information when you access or use the Services. This may include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li><strong>Log Data:</strong> Information such as your IP address, browser type and version, operating system, referring URL, pages visited, time and date of visits, and other interaction data;</li>
                <li><strong>Device Information:</strong> Information about the device you use to access the Services, including device type, unique device identifiers, and mobile network information;</li>
                <li><strong>Location Data:</strong> General geographic location based on your IP address or, with your consent, more precise location data from your mobile device;</li>
                <li><strong>Cookies and Similar Technologies:</strong> Information collected through cookies, web beacons, and similar technologies (see Section 4 for more details).</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                We also use third-party analytics services (such as Google Analytics) which use cookies and similar technologies to collect and analyze information about use of the Services and to report on activities and trends. These services may also collect information regarding your use of other websites, apps, and online resources. You can learn about Google's practices at <a href="https://www.google.com/policies/privacy/partners/" target="_blank" rel="noopener noreferrer" className="text-[#1944FF] font-medium hover:underline">www.google.com/policies/privacy/partners/</a> and opt out by installing the Google Analytics opt-out browser add-on at <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#1944FF] font-medium hover:underline">https://tools.google.com/dlpage/gaoptout</a>.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">2. How Do We Use the Information We Collect?</h2>
              <p className="text-neutral-700 mb-4">We use the information we collect for a variety of purposes, including to:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li><strong>Provide and Improve the Services:</strong> To operate, maintain, and improve the Services, and to develop new features and offerings;</li>
                <li><strong>Process Transactions:</strong> To facilitate investments, process payments, and manage your account;</li>
                <li><strong>Verify Identity:</strong> To verify your identity and comply with KYC (Know Your Customer) and AML (Anti-Money Laundering) requirements;</li>
                <li><strong>Communicate with You:</strong> To send you transactional emails, investment updates, account notifications, and (with your consent) marketing communications;</li>
                <li><strong>Personalize Your Experience:</strong> To tailor content and offerings to your interests and preferences;</li>
                <li><strong>Ensure Security:</strong> To detect and prevent fraud, unauthorized access, and other security issues;</li>
                <li><strong>Comply with Legal Obligations:</strong> To comply with applicable laws, regulations, and legal processes;</li>
                <li><strong>Enforce Our Terms:</strong> To enforce our Terms of Service and other agreements;</li>
                <li><strong>Analyze and Research:</strong> To conduct analytics, research, and reporting to understand how the Services are used and to improve them;</li>
                <li><strong>Customer Support:</strong> To respond to your inquiries and provide customer support.</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">3. Do We Share Your Personal Information?</h2>
              <p className="text-neutral-700 mb-4">
                In connection with the Services, we may share your Personal Information with the following categories of third parties:
              </p>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">3.1 Partners</h3>
              <p className="text-neutral-700 mb-4">
                We may collaborate with other companies (such as broker-dealers, registered investment advisers, custodians, escrow agents, and other financial intermediaries) to deliver the Services. We may share Personal Information as reasonably necessary for such partners to provide their services in connection with the offerings or platform, subject to appropriate contractual protections.
              </p>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">3.2 Affiliates</h3>
              <p className="text-neutral-700 mb-4">
                We may share your information with our affiliates and subsidiaries for purposes consistent with this Policy.
              </p>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">3.3 Service Providers</h3>
              <p className="text-neutral-700 mb-4">
                We engage a variety of third-party service providers to support our operations. These may include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Identity verification and KYC/AML providers;</li>
                <li>Cloud hosting and data storage providers;</li>
                <li>Payment processors and banking partners;</li>
                <li>Email and communication service providers;</li>
                <li>Analytics and marketing platforms;</li>
                <li>Customer support tools;</li>
                <li>Legal, accounting, and audit service providers.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                We provide such service providers with access to Personal Information only as reasonably necessary to perform services on our behalf and subject to contractual obligations to protect your information.
              </p>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">3.4 Payment Providers and Financial Connectivity Tools</h3>
              <p className="text-neutral-700 mb-4">
                We may work with payment and funds-transfer providers (such as Stripe or other similar services) to facilitate payments and investment transactions. These providers may store certain information about you in accordance with their own privacy policies.
              </p>
              <p className="text-neutral-700 mb-4">
                We may also use financial data aggregators (such as Plaid Inc. or similar providers) to connect your bank or investment accounts. If we use Plaid or a similar service:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>You will authorize the provider to retrieve and share your financial information with us;</li>
                <li>Your use of the provider's service is subject to their own privacy policy and terms;</li>
                <li>We receive only the data we need to facilitate transactions or verify accounts;</li>
                <li>We do not store your banking login credentials.</li>
              </ul>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">3.5 Business Transfers and Legal Requirements</h3>
              <p className="text-neutral-700 mb-4">We may transfer your Personal Information:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>In connection with a merger, acquisition, reorganization, sale of assets, or bankruptcy;</li>
                <li>To comply with applicable law, regulation, legal process, or governmental request;</li>
                <li>To enforce our Terms of Service and other agreements;</li>
                <li>To protect the rights, property, or safety of Honeyshares, our users, or others;</li>
                <li>With your consent or at your direction.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                To the extent legally permitted, we will endeavor to notify you of material changes in the processing of your Personal Information due to such events and, where required, offer you an opportunity to consent.
              </p>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">3.6 Fraud and Security</h3>
              <p className="text-neutral-700 mb-4">
                We may share Personal Information with third parties if we believe in good faith that access, use, preservation, or disclosure of such information is reasonably necessary to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Detect, prevent, or address fraud, security, or technical issues;</li>
                <li>Protect against harm to the rights, property, or safety of Honeyshares, our users, or the public;</li>
                <li>Investigate potential violations of our Terms of Service;</li>
                <li>Respond to law enforcement requests or legal process.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                In some cases, this may involve sharing information with databases used for identity verification, AML, and KYC purposes.
              </p>

              <div className="bg-[#F0F0F0] border-2 border-[#242424] rounded-lg p-4 mb-4">
                <p className="text-foreground text-sm">
                  <strong>Note:</strong> The privacy, data security, and use of your information by partners, service providers, and payment processors are governed by their own policies. We are not responsible for the privacy practices of such third parties, though we seek to work with reputable providers and impose appropriate safeguards via contract.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">4. How Do We Collect and Use Tracking Technologies?</h2>
              
              <h3 className="text-xl font-semibold text-neutral-800 mb-3">4.1 Browser or Device Information</h3>
              <p className="text-neutral-700 mb-4">
                Even if you are only browsing the Services, certain information is automatically collected, such as:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Your IP address and general location;</li>
                <li>Browser type and version;</li>
                <li>Operating system;</li>
                <li>Referring website or source;</li>
                <li>Date, time, and duration of visits;</li>
                <li>Pages viewed and links clicked.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                We use this information for purposes such as improving the Services, analyzing usage, and enhancing security.
              </p>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">4.2 Mobile App Usage</h3>
              <p className="text-neutral-700 mb-4">
                When you download and use our mobile app (if applicable), we and our service providers may collect app usage data, such as:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>App opens and session duration;</li>
                <li>Features used and screens visited;</li>
                <li>Device type and operating system;</li>
                <li>Crash reports and error logs;</li>
                <li>Push notification preferences.</li>
              </ul>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">4.3 Cookies</h3>
              <p className="text-neutral-700 mb-4">
                A cookie is a small file placed on your device by a website. We, and third parties we work with, may use cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Remember your login credentials and preferences;</li>
                <li>Authenticate users and prevent fraud;</li>
                <li>Analyze how the Services are used;</li>
                <li>Personalize content and offerings;</li>
                <li>Deliver targeted advertising;</li>
                <li>Measure the effectiveness of marketing campaigns;</li>
                <li>Improve the overall user experience.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                Cookies and similar technologies allow us to collect information such as browser type, time spent on the Services, pages visited, language preferences, unique identifiers, content you view or click, screen gestures or actions, interactions with chat widgets, and other traffic data.
              </p>
              <p className="text-neutral-700 mb-4">
                You can control or limit the use of cookies through your browser settings. Please note, however:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Disabling cookies may affect the functionality of the Services;</li>
                <li>Some cookies are essential for the operation of the Services;</li>
                <li>You may need to adjust settings separately for each browser or device;</li>
                <li>Opting out of advertising cookies does not eliminate advertising, only personalized advertising.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                For more information about managing cookies, visit <a href="https://www.allaboutcookies.org/manage-cookies/" target="_blank" rel="noopener noreferrer" className="text-[#1944FF] font-medium hover:underline">https://www.allaboutcookies.org/manage-cookies/</a>. By using the Services without disabling cookies, you consent to our use of cookies as described in this Policy.
              </p>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">4.4 Web Beacons and Similar Technologies</h3>
              <p className="text-neutral-700 mb-4">
                We may use web beacons (also known as pixel tags) and similar technologies to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Track email opens and clicks;</li>
                <li>Measure the effectiveness of our communications;</li>
                <li>Collect aggregated statistics about usage;</li>
                <li>Deliver and measure advertising.</li>
              </ul>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">4.5 IP Address</h3>
              <p className="text-neutral-700 mb-4">
                Your IP address is automatically logged when you access the Services, along with the time of the visit and the pages visited. We use IP addresses for purposes such as:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Diagnosing server problems;</li>
                <li>Administering the Services;</li>
                <li>Estimating general geographic location;</li>
                <li>Detecting and preventing fraud and security issues;</li>
                <li>Complying with legal requirements.</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">5. Data Retention</h2>
              <p className="text-neutral-700 mb-4">
                We retain your Personal Information for as long as necessary to fulfill the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements. Retention periods vary depending on:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>The nature of the data and the purpose for which it was collected;</li>
                <li>Legal and regulatory requirements;</li>
                <li>The need to resolve disputes and enforce agreements;</li>
                <li>Business and operational needs.</li>
              </ul>
              <p className="text-neutral-700">
                When we no longer need your Personal Information, we will securely delete or anonymize it in accordance with our data retention policies.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">6. Your Rights and Choices</h2>
              <p className="text-neutral-700 mb-4">Depending on your jurisdiction, you may have certain rights regarding your Personal Information, including:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li><strong>Access:</strong> The right to request access to the Personal Information we hold about you;</li>
                <li><strong>Correction:</strong> The right to request correction of inaccurate or incomplete information;</li>
                <li><strong>Deletion:</strong> The right to request deletion of your Personal Information (subject to legal exceptions);</li>
                <li><strong>Portability:</strong> The right to receive a copy of your data in a portable format;</li>
                <li><strong>Opt-Out:</strong> The right to opt out of certain processing activities, such as marketing communications;</li>
                <li><strong>Withdraw Consent:</strong> The right to withdraw consent where processing is based on consent.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                To exercise these rights, please contact us at <a href="mailto:support@honeyshares.com" className="text-[#1944FF] font-medium hover:underline">support@honeyshares.com</a>. We will respond to your request in accordance with applicable law.
              </p>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">7. Security</h2>
              <p className="text-neutral-700 mb-4">
                We implement and maintain reasonable administrative, technical, and physical safeguards designed to protect your Personal Information from unauthorized access, use, disclosure, alteration, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Encryption of data in transit and at rest;</li>
                <li>Access controls and authentication requirements;</li>
                <li>Regular security assessments and testing;</li>
                <li>Employee training and security awareness programs;</li>
                <li>Incident response and breach notification procedures.</li>
              </ul>
              <p className="text-neutral-700">
                However, no method of transmission or storage is 100% secure. We cannot guarantee absolute security of your information.
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">8. Children's Privacy</h2>
              <p className="text-neutral-700">
                The Services are not intended for use by children under the age of 18. We do not knowingly collect Personal Information from children under 18. If we become aware that we have collected Personal Information from a child under 18, we will take steps to delete such information promptly. If you believe we have collected information from a child under 18, please contact us at <a href="mailto:support@honeyshares.com" className="text-[#1944FF] font-medium hover:underline">support@honeyshares.com</a>.
              </p>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">9. International Users</h2>
              <p className="text-neutral-700 mb-4">
                The Services are operated from the United States. If you access the Services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States, where our servers are located and our central database is operated.
              </p>
              <p className="text-neutral-700">
                By using the Services, you consent to the transfer, storage, and processing of your information in the United States, which may have different data protection laws than your country of residence.
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">10. California Privacy Rights</h2>
              <p className="text-neutral-700 mb-4">
                If you are a California resident, you may have additional rights under the California Consumer Privacy Act (CCPA), including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>The right to know what Personal Information we collect, use, disclose, and sell;</li>
                <li>The right to request deletion of your Personal Information;</li>
                <li>The right to opt out of the "sale" of your Personal Information;</li>
                <li>The right to non-discrimination for exercising your privacy rights.</li>
              </ul>
              <p className="text-neutral-700">
                To exercise these rights, please contact us at <a href="mailto:support@honeyshares.com" className="text-[#1944FF] font-medium hover:underline">support@honeyshares.com</a>.
              </p>
            </section>

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">11. Contact Us</h2>
              <p className="text-neutral-700 mb-4">
                If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-secondary rounded-lg p-4">
                <p className="text-foreground font-medium">Honeyshares LLC</p>
                <p className="text-foreground">Email: <a href="mailto:support@honeyshares.com" className="text-[#1944FF] font-medium hover:underline">support@honeyshares.com</a></p>
              </div>
            </section>
          </div>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link to="/terms" className="text-[#1944FF] font-medium hover:underline">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
